/**
 * Extensões da classe nativa `Array` do TypeScript.
 *
 * Este arquivo augmenta globalmente a interface `Array<T>` com métodos
 * utilitários de uso frequente, evitando a necessidade de funções auxiliares
 * espalhadas pela base de código.
 *
 * **Importante:** este arquivo deve ser importado uma única vez na aplicação
 * (ex.: `main.ts`) para que as extensões de protótipo sejam registradas.
 */
export {};

declare global {
    interface Array<T> {
        /**
         * Verifica se o array está vazio (sem nenhum elemento).
         *
         * @returns `true` se o array não possuir elementos, `false` caso contrário.
         *
         * @example
         * [].isEmpty()        // true
         * [1, 2, 3].isEmpty() // false
         */
        isEmpty(): boolean;

        /**
         * Verifica se o array possui ao menos um elemento.
         *
         * É o oposto semântico de `isEmpty()`.
         *
         * @returns `true` se o array possuir pelo menos um elemento, `false` caso contrário.
         *
         * @example
         * [1, 2, 3].isNotEmpty() // true
         * [].isNotEmpty()        // false
         */
        isNotEmpty(): boolean;

        /**
         * Retorna um novo array contendo apenas os elementos que **não** satisfazem
         * o predicado informado. Não modifica o array original.
         *
         * @param predicate Função de teste aplicada a cada elemento. Elementos para os
         *                  quais a função retornar `true` serão **removidos** do resultado.
         * @returns Novo array sem os elementos que satisfazem o predicado.
         *
         * @example
         * [1, 2, 3, 4].removeIf(n => n % 2 === 0) // [1, 3]
         * users.removeIf(u => u.inactive)           // apenas usuários ativos
         */
        removeIf(predicate: (item: T) => boolean): T[];

        /**
         * Retorna um novo array contendo apenas os elementos presentes em **ambos**
         * os arrays, de acordo com a função de comparação fornecida.
         *
         * Equivale a uma operação de **interseção de conjuntos**.
         * Não modifica nenhum dos arrays originais.
         *
         * @param other Array com o qual a interseção será calculada.
         * @param compareFn Função que recebe dois elementos e retorna `true` se forem
         *                  considerados iguais para fins de comparação.
         * @returns Novo array com os elementos em comum entre `this` e `other`.
         *
         * @example
         * const a = [1, 2, 3];
         * const b = [2, 3, 4];
         * a.intersect(b, (x, y) => x === y) // [2, 3]
         *
         * @example
         * // Comparando objetos por ID
         * const selected = allUsers.intersect(activeUsers, (a, b) => a.id === b.id);
         */
        intersect(other: T[], compareFn: (a: T, b: T) => boolean): T[];

        /**
         * Remove do array original (in-place) todos os elementos presentes em `other`,
         * de acordo com a função de comparação fornecida.
         *
         * **Atenção:** diferentemente de `removeIf` e `difference`, este método
         * **modifica o array original** utilizando `splice`.
         *
         * @param other Array contendo os elementos que devem ser removidos.
         * @param compareFn Função que recebe dois elementos e retorna `true` se forem
         *                  considerados iguais para fins de remoção.
         *
         * @example
         * const list = [1, 2, 3, 4];
         * list.removeAll([2, 4], (a, b) => a === b);
         * // list agora é [1, 3]
         *
         * @example
         * // Removendo usuários selecionados de uma lista
         * allUsers.removeAll(selectedUsers, (a, b) => a.id === b.id);
         */
        removeAll(other: T[], compareFn: (a: T, b: T) => boolean): void;

        /**
         * Retorna um novo array contendo apenas os elementos de `this` que
         * **não estão presentes** em `other`, de acordo com a função de comparação.
         *
         * Equivale a uma operação de **diferença de conjuntos** (`this − other`).
         * Não modifica nenhum dos arrays originais.
         *
         * @param other Array cujos elementos devem ser excluídos do resultado.
         * @param compareFn Função que recebe dois elementos e retorna `true` se forem
         *                  considerados iguais para fins de comparação.
         * @returns Novo array com os elementos de `this` ausentes em `other`.
         *
         * @example
         * const a = [1, 2, 3, 4];
         * const b = [2, 4];
         * a.difference(b, (x, y) => x === y) // [1, 3]
         *
         * @example
         * // Itens ainda não selecionados
         * const remaining = allItems.difference(selectedItems, (a, b) => a.id === b.id);
         */
        difference(other: T[], compareFn: (a: T, b: T) => boolean): T[];

        /**
         * Retorna um novo array contendo apenas os elementos que satisfazem
         * a função de comparação fornecida. É um alias semântico para `Array.filter`,
         * com a intenção de tornar o código mais legível em contextos onde a
         * operação representa "manter" elementos em vez de "filtrar" elementos.
         *
         * Não modifica o array original.
         *
         * @param compareFn Função de teste com a mesma assinatura de `Array.filter`:
         *                  recebe o valor, o índice e o array original.
         *                  Elementos para os quais retornar `true` serão mantidos.
         * @returns Novo array com os elementos que passaram no teste.
         *
         * @example
         * [1, 2, 3, 4, 5].keep(n => n > 2)       // [3, 4, 5]
         * users.keep((u, i) => i < 10 && u.active) // primeiros 10 usuários ativos
         */
        keep(compareFn: (value: T, index: number, array: T[]) => boolean): T[];

        /**
         * Retorna o primeiro elemento que satisfaz o predicado informado,
         * ou `undefined` se nenhum elemento atender à condição.
         *
         * É um alias semântico para `Array.find`, com a intenção de tornar
         * o código mais expressivo quando a intenção é obter o **primeiro**
         * elemento que corresponde a um critério.
         *
         * Não modifica o array original.
         *
         * @param predicate Função de teste aplicada a cada elemento. O primeiro
         *                  elemento para o qual retornar `true` será devolvido.
         * @returns O primeiro elemento que satisfaz o predicado, ou `undefined`
         *          caso nenhum elemento seja encontrado.
         *
         * @example
         * [1, 2, 3, 4].first(n => n > 2)          // 3
         * users.first(u => u.role === 'admin')      // primeiro usuário admin
         * [1, 2, 3].first(n => n > 10)             // undefined
         */
        first(predicate: (item: T) => boolean): T | undefined;
    }
}

Array.prototype.isEmpty = function <T>(this: T[]): boolean {
    return this.length === 0;
};

Array.prototype.isNotEmpty = function <T>(this: T[]): boolean {
    return this.length > 0;
};

Array.prototype.removeIf = function <T>(predicate: (item: T) => boolean): T[] {
    return this.filter((item: T) => !predicate(item));
};

Array.prototype.intersect = function <T>(
    this: T[],
    other: T[],
    compareFn: (a: T, b: T) => boolean,
): T[] {
    const result: T[] = [];

    for (const item of this) {
        if (other.some((o) => compareFn(item, o))) {
            result.push(item);
        }
    }

    return result;
};

Array.prototype.removeAll = function <T>(
    this: T[],
    other: T[],
    compareFn: (a: T, b: T) => boolean,
): void {
    for (let i = this.length - 1; i >= 0; i--) {
        if (other.some((b) => compareFn(this[i], b))) {
            this.splice(i, 1); // remove o item no índice i
        }
    }
};

Array.prototype.difference = function <T>(
    this: T[],
    other: T[],
    compareFn: (a: T, b: T) => boolean,
): T[] {
    return this.filter((a) => !other.some((b) => compareFn(a, b)));
};

Array.prototype.keep = function <T>(
    this: T[],
    compareFn: (value: T, index: number, array: T[]) => boolean,
): T[] {
    return this.filter(compareFn);
};

Array.prototype.first = function <T>(
    this: T[],
    predicate: (item: T) => boolean,
): T | undefined {
    return this.find(predicate);
};

